import React, { useState } from 'react';

import FileDownloadIcon from '@mui/icons-material/FileDownload';

import { Button } from '@host/components/NewUI/ComponentsLibrary';

import FileDropzone from './components/FileDropzone';
import Layout from './components/Layout';
import PortalBackButton from './components/PortalBackButton';
import PortalCard from './components/PortalCard';
import PortalFooter from './components/PortalFooter';
import PortalPreviewFrame from './components/PortalPreviewFrame';
import { palette } from './styles';
import { triggerDownload } from './usePortalDownloads';
import { onboardColors } from '../theme/onboardTokens';

import type { IPortalItemRow } from './types';

interface Props {
  item: IPortalItemRow;
  documentUrl: string;
  onBack: () => void;
  // The candidate's own upload, shown once the item is submitted.
  submittedDocument?: React.ReactNode;
  onSubmit: (files: File[]) => Promise<void>;
  onComplete: () => Promise<void>;
}

const LibraryDocumentView: React.FC<Props> = ({
  item,
  documentUrl,
  onBack,
  onSubmit,
  onComplete,
  submittedDocument
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const downloadDocument = () => triggerDownload(documentUrl, item.label);

  const requiresUpload = item.request_return === true;
  const submitted = item.status === 'submitted';

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (requiresUpload) {
        if (files.length === 0) return;
        await onSubmit(files);
      } else {
        await onComplete();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Submission failed');
      setSubmitting(false);
    }
  };

  const submitDisabled = submitting || (requiresUpload && files.length === 0);
  let submitLabel = 'Mark as complete';
  if (submitting) {
    submitLabel = 'Submitting…';
  } else if (requiresUpload) {
    submitLabel = 'Submit';
  }

  return (
    <Layout title={item.label}>
      <PortalCard>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 24
          }}
        >
          <PortalBackButton onClick={onBack} />
          <Button
            buttonVariant="secondary"
            icon={<FileDownloadIcon />}
            id="portal-download-document-button"
            onClick={downloadDocument}
          >
            Download document
          </Button>
        </div>

        <PortalPreviewFrame fileName={item.label} src={documentUrl} />

        {submitted ? submittedDocument : null}

        {requiresUpload && !submitted ? (
          <>
            <p style={{ fontSize: 16, color: palette.black, margin: '24px 0 12px' }}>
              {item.label}
              {item.required ? (
                <span style={{ color: onboardColors.status.warningRed }}>*</span>
              ) : null}
            </p>

            <FileDropzone
              files={files}
              onFiles={(picked) => setFiles((curr) => [...curr, ...picked])}
              onRemove={(index) => setFiles((curr) => curr.filter((_, i) => i !== index))}
            />
          </>
        ) : null}

        {error !== null && (
          <p style={{ color: palette.warning, fontSize: 14, marginTop: 16 }}>{error}</p>
        )}
      </PortalCard>

      {!submitted ? (
        <PortalFooter>
          <Button
            buttonVariant="primary"
            disabled={submitDisabled}
            id="portal-submit-library-document-button"
            onClick={handleSubmit}
          >
            {submitLabel}
          </Button>
        </PortalFooter>
      ) : null}
    </Layout>
  );
};

export default LibraryDocumentView;
